import type { ChangeEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { Box, FormLabel, Group, Textarea, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput, usePublicationSettingUpdateMutation } from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';

const PublicationSettingsDisableWSJTranslation = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  const { mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const handleStringArrayChange =
    (fieldName: keyof PublicationSettingUpdateInput): ChangeEventHandler<HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (currentPublicationSetting === null) {
        return;
      }
      const { value } = e.target as HTMLTextAreaElement;
      const parsed = value
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: parsed });
      setHasChanged(true);
    };

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    const newPublicationSetting: PublicationSettingUpdateInput = { ...currentPublicationSetting };

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: async () => {
          setCurrentPublicationSetting(newPublicationSetting);
          setHasChanged(false);
          alertSuccess('Publication settings saved successfully');

          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  if (currentProperty !== 'wsj') {
    return (
      <Group gap="lg" direction="column">
        <Group.Item fullWidth>
          <Typography variant="note">
            This setting is only available on WSJ. Switch to the WSJ property to edit it.
          </Typography>
        </Group.Item>
      </Group>
    );
  }

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>Disable WSJ translation</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  Allesseh IDs listed here will skip the WSJ Mobile (Pubcrawl) translation publish. The standard
                  Allesseh publish still runs, but no companion &quot;_WSJ_MOBILE_TRANSLATED&quot; collection is written
                  — use this for pages that don&apos;t have a mobile equivalent (e.g. desktop-only homepages) or for
                  sections whose treatment types aren&apos;t mapped in the translation config.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Disable WSJ Translation" labelPosition="top" fullWidth />
                    <Textarea
                      style={{ height: 800 }}
                      placeholder="One Allesseh ID per line (or comma-separated)"
                      value={(currentPublicationSetting.disableWSJTranslation ?? []).join('\n')}
                      onChange={handleStringArrayChange('disableWSJTranslation')}
                      disabled={isReadOnly}
                    />
                  </Group>
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsDisableWSJTranslation;
